import React, { useState } from 'react';
import { Package, Wrench, Beaker } from 'lucide-react';
import Icon from '../../../components/AppIcon';


const HazardMapping = ({ searchQuery, filterLocation }) => {
  const [selectedCategory, setSelectedCategory] = useState('equipment');

  const equipmentHazards = [
    {
      id: 'EQ-001',
      name: 'Compressor COMP-01',
      location: 'offshore-alpha',
      hazards: [
        { type: 'Mechanical Failure', count: 8, severity: 'high' },
        { type: 'Pressure Excursion', count: 5, severity: 'critical' },
        { type: 'Vibration', count: 3, severity: 'medium' },
      ],
      totalRisk: 'high',
    },
    {
      id: 'EQ-002',
      name: 'Storage Tank TK-201',
      location: 'refinery-01',
      hazards: [
        { type: 'Overfill', count: 4, severity: 'critical' },
        { type: 'Corrosion', count: 6, severity: 'high' },
        { type: 'Leak', count: 7, severity: 'medium' },
      ],
      totalRisk: 'critical',
    },
  ];

  const taskHazards = [
    {
      id: 'TASK-001',
      name: 'Hot Work Operations',
      frequency: 'High',
      hazards: [
        { type: 'Fire/Explosion', count: 12, severity: 'critical' },
        { type: 'Burns', count: 8, severity: 'high' },
        { type: 'Smoke Inhalation', count: 5, severity: 'medium' },
      ],
      totalRisk: 'critical',
    },
    {
      id: 'TASK-002',
      name: 'Confined Space Entry',
      frequency: 'Medium',
      hazards: [
        { type: 'Asphyxiation', count: 10, severity: 'critical' },
        { type: 'Toxic Exposure', count: 7, severity: 'high' },
        { type: 'Entrapment', count: 4, severity: 'high' },
      ],
      totalRisk: 'critical',
    },
  ];

  const chemicalHazards = [
    {
      id: 'CHEM-001',
      name: 'Hydrogen Sulfide (H2S)',
      casNumber: '7783-06-4',
      properties: {
        flammability: 'high',
        toxicity: 'critical',
        reactivity: 'medium',
      },
      hazards: [
        { type: 'Toxic Inhalation', severity: 'critical' },
        { type: 'Flammable Gas', severity: 'high' },
        { type: 'Corrosive', severity: 'medium' },
      ],
      exposureLimit: '10 ppm (TWA)',
      totalRisk: 'critical',
    },
    {
      id: 'CHEM-002',
      name: 'Crude Oil',
      casNumber: '8002-05-9',
      properties: {
        flammability: 'high',
        toxicity: 'medium',
        reactivity: 'low',
      },
      hazards: [
        { type: 'Fire Hazard', severity: 'high' },
        { type: 'Skin Contact', severity: 'medium' },
        { type: 'Environmental', severity: 'high' },
      ],
      exposureLimit: '5 mg/m³',
      totalRisk: 'high',
    },
  ];

  const categories = [
    { id: 'equipment', label: 'Equipment', icon: Package, data: equipmentHazards },
    { id: 'tasks', label: 'Tasks', icon: Wrench, data: taskHazards },
    { id: 'chemicals', label: 'Chemicals', icon: Beaker, data: chemicalHazards },
  ];

  const getRiskColor = (level) => {
    const colors = {
      critical: 'bg-red-100 text-red-800 border-red-300',
      high: 'bg-orange-100 text-orange-800 border-orange-300',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      low: 'bg-green-100 text-green-800 border-green-300',
    };
    return colors?.[level] || colors?.medium;
  };

  const currentData = categories?.find(cat => cat?.id === selectedCategory)?.data || [];

  return (
    <div className="space-y-6">
      {/* Category Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {categories?.map((category) => {
          const Icon = category?.icon;
          return (
            <button
              key={category?.id}
              onClick={() => setSelectedCategory(category?.id)}
              className={`
                flex items-center gap-2 px-4 py-3 rounded-lg font-medium whitespace-nowrap
                ${selectedCategory === category?.id
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }
              `}
            >
              <Icon className="w-5 h-5" />
              {category?.label}
            </button>
          );
        })}
      </div>
      {/* Equipment Mapping */}
      {selectedCategory === 'equipment' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {equipmentHazards?.map((equipment) => (
            <div key={equipment?.id} className="bg-white rounded-lg shadow-md p-6 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold text-gray-900">{equipment?.name}</h3>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{equipment?.id}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getRiskColor(equipment?.totalRisk)}`}>
                  {equipment?.totalRisk?.toUpperCase()}
                </span>
              </div>

              <div className="space-y-3">
                {equipment?.hazards?.map((hazard, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="font-medium text-sm text-gray-900">{hazard?.type}</div>
                      <div className="text-xs text-gray-600 mt-1">{hazard?.count} identified instances</div>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getRiskColor(hazard?.severity)}`}>
                      {hazard?.severity}
                    </span>
                  </div>
                ))}
              </div>

              <div className="pt-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Location: <span className="font-medium">{equipment?.location}</span>
                </div>
                <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                  View Details →
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Task Mapping */}
      {selectedCategory === 'tasks' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {taskHazards?.map((task) => (
            <div key={task?.id} className="bg-white rounded-lg shadow-md p-6 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <Wrench className="w-5 h-5 text-purple-600" />
                    <h3 className="font-semibold text-gray-900">{task?.name}</h3>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Frequency: <span className="font-medium">{task?.frequency}</span>
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getRiskColor(task?.totalRisk)}`}>
                  {task?.totalRisk?.toUpperCase()}
                </span>
              </div>

              <div className="space-y-3">
                {task?.hazards?.map((hazard, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="font-medium text-sm text-gray-900">{hazard?.type}</div>
                      <div className="text-xs text-gray-600 mt-1">{hazard?.count} incidents recorded</div>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getRiskColor(hazard?.severity)}`}>
                      {hazard?.severity}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Chemical Mapping */}
      {selectedCategory === 'chemicals' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {chemicalHazards?.map((chemical) => (
            <div key={chemical?.id} className="bg-white rounded-lg shadow-md p-6 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <Beaker className="w-5 h-5 text-green-600" />
                    <h3 className="font-semibold text-gray-900">{chemical?.name}</h3>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">CAS: {chemical?.casNumber}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getRiskColor(chemical?.totalRisk)}`}>
                  {chemical?.totalRisk?.toUpperCase()}
                </span>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <div className="text-xs text-gray-600 mb-1">Flammability</div>
                  <div className={`text-sm font-semibold ${chemical?.properties?.flammability === 'critical' || chemical?.properties?.flammability === 'high' ? 'text-red-700' : 'text-gray-700'}`}>
                    {chemical?.properties?.flammability?.toUpperCase()}
                  </div>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded-lg">
                  <div className="text-xs text-gray-600 mb-1">Toxicity</div>
                  <div className={`text-sm font-semibold ${chemical?.properties?.toxicity === 'critical' || chemical?.properties?.toxicity === 'high' ? 'text-purple-700' : 'text-gray-700'}`}>
                    {chemical?.properties?.toxicity?.toUpperCase()}
                  </div>
                </div>
                <div className="text-center p-3 bg-orange-50 rounded-lg">
                  <div className="text-xs text-gray-600 mb-1">Reactivity</div>
                  <div className={`text-sm font-semibold ${chemical?.properties?.reactivity === 'critical' || chemical?.properties?.reactivity === 'high' ? 'text-orange-700' : 'text-gray-700'}`}>
                    {chemical?.properties?.reactivity?.toUpperCase()}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                {chemical?.hazards?.map((hazard, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="text-sm text-gray-900">{hazard?.type}</div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getRiskColor(hazard?.severity)}`}>
                      {hazard?.severity}
                    </span>
                  </div>
                ))}
              </div>

              <div className="pt-3 border-t border-gray-200">
                <div className="text-xs text-gray-600">
                  Exposure Limit: <span className="font-medium text-gray-900">{chemical?.exposureLimit}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HazardMapping;